import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../AdminContext'
import { clone, uniqueSlug } from '../editing'
import { emptyIssue } from './newsletterRules'
import type { NewsletterIssue } from '../../core/types/newsletter'

/** Nieuwsbrieven aanmaken, wijzigen en verwijderen in het concept van het beheer (zoals useSocialPosts). */
export function useNewsletters() {
  const { marketing, setMarketing } = useAdmin()
  const navigate = useNavigate()
  const issues = marketing.newsletters

  const createIssue = useCallback(
    (patch: Partial<NewsletterIssue> = {}) => {
      const id = uniqueSlug(`nieuwsbrief-${Date.now().toString(36)}`, issues.map((n) => n.id))
      const issue = emptyIssue({ ...patch, id })
      setMarketing((m) => ({ ...m, newsletters: [issue, ...m.newsletters] }))
      navigate(`/be/mail/nieuwsbrief/${id}`)
      return id
    },
    [issues, setMarketing, navigate],
  )

  const updateIssue = useCallback(
    (id: string, patch: Partial<NewsletterIssue>) =>
      setMarketing((m) => ({ ...m, newsletters: m.newsletters.map((n) => (n.id === id ? { ...n, ...patch } : n)) })),
    [setMarketing],
  )

  /** Haalt de nieuwsbrief weg en geeft een functie die hem op zijn oude plek terugzet. */
  const removeIssue = useCallback(
    (id: string) => {
      const at = issues.findIndex((n) => n.id === id)
      const removed = issues[at]
      setMarketing((m) => ({ ...m, newsletters: m.newsletters.filter((n) => n.id !== id) }))
      return () => {
        if (!removed) return
        setMarketing((m) => {
          if (m.newsletters.some((n) => n.id === id)) return m
          const next = [...m.newsletters]
          next.splice(Math.min(at, next.length), 0, removed)
          return { ...m, newsletters: next }
        })
      }
    },
    [issues, setMarketing],
  )

  const duplicateIssue = useCallback(
    (issue: NewsletterIssue) =>
      createIssue({ ...clone(issue), subject: `${issue.subject} (kopie)`, status: 'concept', sendDate: '', delivery: null }),
    [createIssue],
  )

  return { issues, createIssue, updateIssue, removeIssue, duplicateIssue }
}
